import { FC, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useToast } from '@/providers/Toast.provider';
import { Box, Card, formattedCurrency, ACHWithdrawTransactionResponse, withStyles, capitalizeFirstLetter } from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus, ExpandableText } from 'UI';
import useTransactionApproval from './useTransactionApproval';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: ACHWithdrawTransactionResponse;
  Notes: string;
  isParent?: boolean;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

const ACHFundingTransactionInfo: FC<TransactionInfoProps> = ({ transaction, Notes, isParent = false }) => {
  const toast = useToast();
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ACHWithdrawalTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const { allApprovalTransactions } = useApprovalWorkflow();
  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval();

  const isApprovalRequested = useMemo(() => {
    return allApprovalTransactions.some((t) => t.ID.toString() === transaction.TransactionID);
  }, [transaction.TransactionID, allApprovalTransactions]);

  useEffect(() => {
    if (!isApprovalRequested) return;

    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transaction.TransactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [isApprovalRequested]);

  const receiverName = transaction.FirstName ? transaction.FirstName + transaction.LastName : transaction.CompanyName;

  const transactionDetails = [
    {
      label: t('transactionStatus'),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('holdReleaseDate', { defaultValue: 'Hold Release Date' }),
      value: transaction.HoldReleaseDateTime,
    },
    {
      label: t('type', { defaultValue: 'Transaction Type' }),
      value: t('ACHWithdrawal'),
    },
    {
      label: t('glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.wireReferenceNumber', { defaultValue: 'Wire Reference Number' }),
      value: transaction.WireReferenceNumber || '-',
    },
    {
      label: t('secCode', { defaultValue: 'SEC Code' }),
      value: transaction.SecCode || '',
    },
    {
      label: t('holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
  ];

  const approvalHistoryDetails =
    approvalHistory.length > 0
      ? approvalHistory.flatMap((item) => [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: item.Username,
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: (
              <Box flx="col" gapSize="11px" ai="fe">
                <TransactionStatus
                  status={item.ApprovalDecision.toLowerCase()}
                  label={capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                      defaultValue: item.ApprovalDecision,
                    }),
                  )}
                />
                <div>{item.DecisionDate}</div>
              </Box>
            ),
          },
          {
            label: t('ApprovalHistoryDetails.reason', { defaultValue: 'Reason' }),
            value: <ExpandableText text={item.Reason || ''} />,
            visible: item.Reason !== null,
          },
        ])
      : [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: '-',
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: '-',
          },
        ];

  const recipientDetails = [
    {
      label: t('name', { defaultValue: 'Name' }),
      value: receiverName,
    },
    {
      label: t('address', { defaultValue: 'Address' }),
      value: transaction.Address1 || transaction.Address2 || '-',
    },
    {
      label: t('city', { defaultValue: 'City' }),
      value: transaction.City || '-',
    },
    {
      label: t('postalCodeZipcode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction['PostalCode/Zipcode'] || '-',
    },
    {
      label: t('country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
    {
      label: t('primaryContact', { defaultValue: 'Primary Contact' }),
      value: transaction.PhoneNumber || '-',
    },
  ];

  const bankAccountDetails = [
    {
      label: t('abaRoutingNumber', { defaultValue: 'ABA Routing Number' }),
      value: transaction.ABARoutingNumber || '-',
    },
    {
      label: t('accountNumber', { defaultValue: 'Account Number' }),
      value: transaction.AccountNumber.replaceAll('*', '•') || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('amount', { defaultValue: 'Amount' }),
      value: transaction.Amount ? `${transaction.Currency} ${formattedCurrency(transaction.Amount)}` : '-',
    },
    {
      label: t('failureReason', { defaultValue: 'Failure Reason' }),
      value: state.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={receiverName}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={'ACH Withdrawal'}
        transactionDate={transaction.TransactionDateTime}
        lastModified={transaction.LastModified}
        failureReason={transaction.FailureReason}
        isApprovalRequested={isApprovalRequested}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetails', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {isApprovalRequested && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('ApprovalHistoryDetails.title', { defaultValue: 'Approval History' })}
          </Title>
          <DetailsRows className={cn('rows')} details={approvalHistoryDetails} />
        </Card>
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('recipientInformationTitle', { defaultValue: 'Recipient Information' })}
        </Title>
        <DetailsRows className={cn('rows')} details={recipientDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('bankAccountDetails', { defaultValue: 'Bank Account Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={bankAccountDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfo', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default ACHFundingTransactionInfo;
